import React, { useEffect, useRef } from 'react';


const settings = {
  BOIDS: 70,
  MAX_SPEED: 2,
  MAX_FORCE: 0.15,
  MAX_SIZE: 5,
  DESIRED_SEPARATION: 6,
  NEIGHBOR_RADIUS: 50,
  SEPARATION_WEIGHT: 2,
  ALIGNMENT_WEIGHT: 1,
  COHESION_WEIGHT: 1,
  AVOID_MOUSE: true,
  MOUSE_POSITION: null,
  MOUSE_RADIUS: 40,
  MOUSE_SIGN: -1,
  MOUSE_FORCE: 0.10,
  BOID_STYLE: 0,
  DRAW_TRAILS: true
};

class Vector {
  constructor(x = 0, y = 0) {
    this.x = x;
    this.y = y;
  }
  duplicate() {
    return new Vector(this.x, this.y);
  }
  magnitude() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }
  normalize() {
    const m = this.magnitude();
    if (m > 0) this.divide(m);
    return this;
  }
  limit(max) {
    if (this.magnitude() > max) {
      this.normalize();
      return this.multiply(max);
    }
    return this;
  }
  add(other) {
    this.x += other.x;
    this.y += other.y;
    return this;
  }
  subtract(other) {
    this.x -= other.x;
    this.y -= other.y;
    return this;
  }
  multiply(n) {
    this.x *= n;
    this.y *= n;
    return this;
  }
  divide(n) {
    this.x /= n;
    this.y /= n;
    return this;
  }
  distance(other) {
    const dx = this.x - other.x;
    const dy = this.y - other.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
  static subtract(a, b) {
    return a.duplicate().subtract(b);
  }
  static add(a, b) {
    return a.duplicate().add(b);
  }
  static multiply(a, b) {
    return a.duplicate().multiply(b);
  }
  static divide(a, b) {
    return a.duplicate().divide(b);
  }
}

class Boid {
  constructor(location, velocity, ctx) {
    this.loc = location.duplicate();
    this.velocity = new Vector(Math.random() * 2 - 1, Math.random() * 2 - 1);
    this.ctx = ctx;
  }

  step(neighbors) {
    const acceleration = this.flock(neighbors).add(this.influence());
    this.velocity.add(acceleration).limit(settings.MAX_SPEED);
    this.loc.add(this.velocity);
    this.wrapToCanvasBounds();
  }

  wrapToCanvasBounds() {
    const { width, height } = this.ctx.canvas;
    this.loc.x = this.loc.x < 0 ? width : this.loc.x > width ? 0 : this.loc.x;
    this.loc.y = this.loc.y < 0 ? height : this.loc.y > height ? 0 : this.loc.y;
  }

  flock(neighbors) {
    const separation = this.separate(neighbors).multiply(settings.SEPARATION_WEIGHT);
    const alignment = this.align(neighbors).multiply(settings.ALIGNMENT_WEIGHT);
    const cohesion = this.cohere(neighbors).multiply(settings.COHESION_WEIGHT);
    return separation.add(alignment).add(cohesion);
  }

  cohere(neighbors) {
    const sum = new Vector(0, 0);
    let count = 0;
    for (const boid of neighbors) {
      const d = this.loc.distance(boid.loc);
      if (d > 0 && d < settings.NEIGHBOR_RADIUS) {
        sum.add(boid.loc);
        count++;
      }
    }
    return count > 0 ? this.steer_to(sum.divide(count)) : sum;
  }

  steer_to(target) {
    const desired = Vector.subtract(target, this.loc);
    const d = desired.magnitude();
    if (d === 0) return new Vector(0, 0);
    desired.normalize();
    desired.multiply(d < 100 ? settings.MAX_SPEED * (d / 100) : settings.MAX_SPEED);
    return desired.subtract(this.velocity).limit(settings.MAX_FORCE);
  }

  align(neighbors) {
    const mean = new Vector();
    let count = 0;
    for (const boid of neighbors) {
      const d = this.loc.distance(boid.loc);
      if (d > 0 && d < settings.NEIGHBOR_RADIUS) {
        mean.add(boid.velocity);
        count++;
      }
    }
    if (count > 0) mean.divide(count);
    return mean.limit(settings.MAX_FORCE);
  }

  separate(neighbors) {
    const mean = new Vector();
    let count = 0;
    for (const boid of neighbors) {
      const d = this.loc.distance(boid.loc);
      if (d > 0 && d < settings.DESIRED_SEPARATION) {
        mean.add(Vector.subtract(this.loc, boid.loc).normalize().divide(d));
        count++;
      }
    }
    if (count > 0) mean.divide(count);
    return mean;
  }

  influence() {
    const g = new Vector();
    if (settings.AVOID_MOUSE && settings.MOUSE_POSITION) {
      const mouse = Vector.subtract(settings.MOUSE_POSITION, this.loc);
      let d = mouse.magnitude() - settings.MOUSE_RADIUS;
      d = Math.max(d, 0.01);
      if (d < settings.NEIGHBOR_RADIUS * 5) {
        g.add(mouse.normalize().divide(d * d).multiply(settings.MOUSE_SIGN).limit(settings.MOUSE_FORCE));
      }
    }
    return g;
  }

  draw() {
    const color = '#9290C3';
    this.ctx.lineWidth = 2;
    this.ctx.strokeStyle = color;
    this.ctx.beginPath();
    this.ctx.moveTo(this.loc.x, this.loc.y);
    this.ctx.lineTo(
      this.loc.x + Math.min(this.velocity.x * 5, settings.MAX_SIZE),
      this.loc.y + Math.min(this.velocity.y * 5, settings.MAX_SIZE)
    );
    this.ctx.stroke();
  }
}

const BoidFlockingSim = () => {
  const canvasRef = useRef();
  const flockRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d');
    const boids = Array.from({ length: settings.BOIDS }, () =>
      new Boid(
        new Vector(Math.random() * canvas.width, Math.random() * canvas.height),
        new Vector(Math.random() * 10, Math.random() * 10),
        ctx
      )
    );
    flockRef.current = { ctx, boids, current_time: Date.now(), animationFrame: null };

    const animate = (time) => {
      const flock = flockRef.current;
      const dt = time - flock.current_time;
      flock.current_time = time;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (settings.AVOID_MOUSE && settings.MOUSE_POSITION) {
        ctx.beginPath();
        ctx.arc(
          settings.MOUSE_POSITION.x,
          settings.MOUSE_POSITION.y,
          settings.MOUSE_RADIUS,
          0,
          2 * Math.PI
        );
        ctx.fillStyle = 'rgba(255, 255, 255, 0.005)';
        ctx.fill();
      }

      for (const boid of flock.boids) {
        boid.step(flock.boids);
        boid.draw();
      }

      flock.animationFrame = requestAnimationFrame(animate);
    };

    const onMouseMove = (e) => {
      settings.MOUSE_POSITION = new Vector(e.clientX, e.clientY);
    };

    // const onMouseDown = (e) => {
    //   if (e.button === 0 || e.button === 1) {
    //     settings.AVOID_MOUSE = !settings.AVOID_MOUSE;
    //   }
    // };

    const onResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    settings.MOUSE_POSITION = new Vector(-9001, 0);
    window.addEventListener('mousemove', onMouseMove);
    // window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('resize', onResize);

    animate(Date.now());

    return () => {
      cancelAnimationFrame(flockRef.current.animationFrame);
      window.removeEventListener('mousemove', onMouseMove);
      // window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="c"
      style={{
        display: 'block',
        position: 'fixed', // Fix the canvas to the viewport
        top: 0,
        left: 0,
        width: '100vw', // Full width
        height: '100vh', // Full height
        zIndex: -1, // Send it behind other elements
      }}
    />
  );
};

export default BoidFlockingSim;
